import {ColDef, ICellRendererParams} from 'ag-grid-community'
import {formatCurrency} from '@/services/formatter.service'
import {CsvGridComponentApi} from '@/incoming-balance/types/CsvGridComponentApi.type'
import {computed, ComputedRef, ref} from 'vue'
import _ from 'lodash'
import {fetchBookkeepingAccount, findAccountByNo, updateMultipleAccountActiveStatus, upsertBookkeepingAccount} from '@/bookkeeping-account/bookkeepingAccount.service'
import {buildIncomingBalanceData, calculateTotal, findTotalFromColMatch, getPostStatus} from '@/incoming-balance/incomingBalance.composable'
import {parseNumber} from '@/services/numberParser.service'
import {CsvData} from '@/incoming-balance/types/CsvData.type'
import {CsvDataRow} from '@/incoming-balance/types/CsvDataRow.type'
import {getValue} from '@/incoming-balance/CsvColumnMapper.service'
import {notifyError} from '@/services/infrastructure/notification.service'
import {BookkeepingAccount} from '@/bookkeeping-account/BookkeepingAccount'
import {AccountEntry} from '@/incoming-balance/types/AccountEntry.type'
import {useVatService} from '@/vat/vat.service'
import VatHeaderComponent from '@/incoming-balance/VatHeaderComponent.vue'
import VatCell from '@/incoming-balance/VatCell.vue'

const {getVatOptionById} = useVatService()

const defaultColMap = {
    accountNo: 1,
    accountName: 2,
    vat: 6,
    balance: 5
}

const tribalColConfig: ColDef[] = [
    {
        headerName: 'Kontonr',
        field: 'accountNo'
    },
    {
        headerName: 'Kontonavn',
        field: 'accountName'
    },
    {
        headerName: 'MVA',
        headerComponent: VatHeaderComponent,
        field: 'vat',
        cellRendererSelector: (params: ICellRendererParams) => {
            if(tribalData.value.firstRowIsHeader && params.node.rowIndex === 0) return
            return {
                component: VatCell
            }
        }
    },
    {
        headerName: 'Utgående saldo',
        field: 'balance',
        cellClass: 'text-right',
        cellRenderer: ({value}: ICellRendererParams) => {
            if(!value) return
            value = value.replace(',', '.').replace(/\s+/g, '')
            const parsedFloat = parseFloat(value)
            if (isNaN(parsedFloat)) {
                return value
            } else {
                return formatCurrency(parsedFloat)
            }
        }
    }
]
const tribalData = ref<CsvGridComponentApi>(
    buildIncomingBalanceData(defaultColMap, tribalColConfig)
)

const mapCsvToEntries = (data: any): AccountEntry[] => {
    const {csvData, columnMapping} = data.value
    if (!csvData || csvData.length === 0) return []

    return csvData.map((row: any, rowIndex: number) => {
        const accountNo = row[columnMapping['accountNo']]
        const accountName = row[columnMapping['accountName']]
        const balance = parseNumber(row[columnMapping['balance']])
        const transStatus = getPostStatus(balance, accountNo, false)

        return {
            accountNo,
            accountName,
            balance,
            rowIndex,
            transStatus
        }
    })
}

const useTrialBalance = () => {

    const totalBalance = computed(() => calculateTotal(tribalData, 'balance'))
    const customerTotal = computed(() => findTotalFromColMatch(tribalData, 'accountNo', '1500', 'balance'))
    const supplierTotal = computed(() => findTotalFromColMatch(tribalData, 'accountNo', '2400', 'balance'))
    const entries = computed(() => mapCsvToEntries(tribalData))


    //MISSING
    const isAccountMissing = (row: CsvDataRow): boolean => {
        const accountNo = getValue('accountNo', row, tribalData.value.columnMapping) as string
        if (!accountNo || !accountNo.match(/^\d{4}$/)) return false
        return !findAccountByNo(accountNo)
    }

    const missingAccounts= computed<number>(() => {
        const missing = _(tribalData.value.csvData)
            .compact()
            .reject((row: CsvDataRow | null) => row === null)
            .reject((_, index: number) => {
                return tribalData.value.firstRowIsHeader && index === 0
            })
            .filter(isAccountMissing)
            .uniqBy((row: CsvDataRow) => {
                return getValue('accountNo', row, tribalData.value.columnMapping) as string
            })
            .value()
        return missing.length
    })


    const createAccount = async (row: CsvDataRow) => {
        const accountNo = getValue('accountNo', row, tribalData.value.columnMapping) as string
        const accountName = getValue('accountName', row, tribalData.value.columnMapping) as string
        const vatId = getValue('vat', row, tribalData.value.columnMapping) as string

        const vatOption = getVatOptionById(parseInt(vatId))

        if (!accountNo.match(/^\d{4}$/)) {
            console.error('Invalid account number', accountNo)
            notifyError({ title: 'Feilet', description: `Kontonummeret (${accountNo}) må være fire siffer.`})
            return
        }
        const newAccount: BookkeepingAccount = {
            accountNo: accountNo,
            name: accountName,
            active: true,
            description: accountName,
            keywords: [],
            vatSuggestionId: vatOption?.id || 1,
            isAccrual: false
        }
        await upsertBookkeepingAccount(newAccount)
        await fetchBookkeepingAccount()
    }

    const createMissingAccounts = async () => {
        for (const [index, row] of tribalData.value.csvData.entries()) {
            if (tribalData.value.firstRowIsHeader && index === 0) continue
            if(isAccountMissing(row)) await createAccount(row)
        }
    }

    //MISNAMED
    const isAccountMisnamed = (row: CsvDataRow): boolean => {
        const accountNo = getValue('accountNo', row, tribalData.value.columnMapping) as string
        const accountName = getValue('accountName', row, tribalData.value.columnMapping) as string

        const systemAccount = findAccountByNo(accountNo)
        return systemAccount && systemAccount.name !== accountName
    }

    const misnamedAccounts: ComputedRef<number> = computed(() => {
        const misnamed = _(tribalData.value.csvData)
            .compact()
            .reject((row: CsvDataRow | null) => row === null)
            .filter(isAccountMisnamed)
            .uniqBy((row: CsvDataRow) => {
                return getValue('accountNo', row, tribalData.value.columnMapping) as string
            })
            .value()

        return misnamed.length
    })

    const correctMisnamedAccount = async (row: CsvDataRow) => {
        if(isAccountMisnamed(row)) {
            const accountNo = getValue('accountNo', row, tribalData.value.columnMapping) as string
            const accountName = getValue('accountName', row, tribalData.value.columnMapping) as string
            const existingAccount = findAccountByNo(accountNo)
            await upsertBookkeepingAccount({
                ...existingAccount,
                name: accountName
            })
        }
    }

    const correctMisnamedAccounts = async () => {
        const promises = []
        for (const [index, row] of tribalData.value.csvData.entries()) {
            if (tribalData.value.firstRowIsHeader && index === 0) continue
            promises.push(correctMisnamedAccount(row))
        }
        await Promise.all(promises)
        await fetchBookkeepingAccount()
    }

    // INACTIVE
    const accountNeedsActivation = (row: CsvDataRow): boolean => {
        const accountNo = getValue('accountNo', row, tribalData.value.columnMapping) as string
        const systemAccount = findAccountByNo(accountNo)
        return systemAccount ? !systemAccount.active : false
    }

    const inactiveAccounts = computed<number>(() => {
        const inactive = _(tribalData.value.csvData)
            .compact()
            .reject((row: CsvDataRow | null) => row === null)
            .filter(accountNeedsActivation)
            .uniqBy((row: CsvDataRow) => {
                return getValue('accountNo', row, tribalData.value.columnMapping) as string
            })
            .value()
        return inactive.length
    })

    const activateAccount = async (row: CsvDataRow) => {
        const accountNo = getValue('accountNo', row, tribalData.value.columnMapping) as string
        if(!accountNo) return
        await updateMultipleAccountActiveStatus([accountNo], true)
    }

    const activateAllInactiveAccounts = async () => {
        const accountNos = []
        for (const [index, row] of tribalData.value.csvData.entries()) {
            if (tribalData.value.firstRowIsHeader && index === 0) continue
            if (accountNeedsActivation(row)) {
                const accountNo = getValue('accountNo', row, tribalData.value.columnMapping) as string
                accountNos.push(accountNo)
            }
        }
        await updateMultipleAccountActiveStatus(accountNos, true)
        await fetchBookkeepingAccount()
    }

    // VAT CODE MISMATCH

    const isVatMismatch = (row: CsvDataRow): boolean => {
        const vatId = getValue('vat', row, tribalData.value.columnMapping) as string
        if(!vatId) return false

        const accountNo = getValue('accountNo', row, tribalData.value.columnMapping) as string
        const systemAccount = findAccountByNo(accountNo)
        if(!systemAccount) return false
        const systemVatOption = getVatOptionById(systemAccount.vatSuggestionId)
        if(!systemVatOption) return false

        return systemVatOption.id.toString() !== vatId.toString()
    }

    const mismatchedVatCodes = computed<number>(() => {
        const mismatched = _(tribalData.value.csvData)
            .compact()
            .reject((row: CsvDataRow | null) => row === null)
            .filter(isVatMismatch)
            .uniqBy((row: CsvDataRow) => {
                return getValue('vat', row, tribalData.value.columnMapping) as string
            })
            .value()

        return mismatched.length
    })

    const correctMismatchedVatCode = async (row: CsvDataRow) => {
        if(isVatMismatch(row)) {
            const accountNo = getValue('accountNo', row, tribalData.value.columnMapping) as string
            const existingAccount = findAccountByNo(accountNo)
            const vatId = getValue('vat', row, tribalData.value.columnMapping) as string

            const vatOption = getVatOptionById(parseInt(vatId))

            if(!vatOption) {
                return
            }

            await upsertBookkeepingAccount({
                ...existingAccount,
                vatSuggestionId: vatOption.id
            })
        }
    }

    const correctMismatchedVatCodes = async () => {
        const promises = []
        for (const [index, row] of tribalData.value.csvData.entries()) {
            if (tribalData.value.firstRowIsHeader && index === 0) continue
            promises.push(correctMismatchedVatCode(row))
        }
        await Promise.all(promises)
        await fetchBookkeepingAccount()
    }

    const getSystemVatOption = (row: CsvDataRow) => {
        const accountNo = getValue('accountNo', row, tribalData.value.columnMapping) as string
        const systemAccount = findAccountByNo(accountNo)
        if(!systemAccount) return
        return getVatOptionById(systemAccount.vatSuggestionId)
    }

    const setCsvData = (data: CsvData) => {
        tribalData.value.csvData = (data as string[][])
            .filter(row => row.some(cell => cell.trim() !== ''))
    }

    const clear = () => {
        tribalData.value.csvData = []
        tribalData.value.fileName = ''
        tribalData.value.columnMapping = defaultColMap
    }

    return {
        tribalData,
        data: tribalData,
        entries,
        totalBalance,
        customerTotal,
        supplierTotal,

        //MISSING
        missingAccounts,
        isAccountMissing,
        createAccount,
        createMissingAccounts,

        //MISNAMED
        misnamedAccounts,
        isAccountMisnamed,
        correctMisnamedAccount,
        correctMisnamedAccounts,

        //INACTIVE
        inactiveAccounts,
        accountNeedsActivation,
        activateAccount,
        activateAllInactiveAccounts,

        //VAT CODE MISMATCH
        mismatchedVatCodes,
        isVatMismatch,
        correctMismatchedVatCode,
        correctMismatchedVatCodes,

        getSystemVatOption,

        clear,
        setCsvData
    }
}

export default useTrialBalance
